import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, Clock, Wallet, FileText,
  BarChart3, ShieldCheck, Settings, Search, Bell, ChevronDown, LogOut, Menu, X, Rocket, ScanFace,
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import BookDemoDialog from "./BookDemoDialog";

const nav = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/employees", label: "Employees", icon: Users },
  { to: "/app/leave", label: "Leave", icon: CalendarDays },
  { to: "/app/attendance", label: "Attendance", icon: Clock },
  { to: "/app/time", label: "Time", icon: ScanFace },
  { to: "/app/payroll", label: "Payroll", icon: Wallet },
  { to: "/app/documents", label: "Documents", icon: FileText },
  { to: "/app/reports", label: "Reports", icon: BarChart3 },
  { to: "/app/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function SidebarContent({ onNavigate, onDemo }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 h-[76px] flex items-center shrink-0">
        <Logo size="sm" showTagline={false} />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-brand-light text-brand-blue" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 shrink-0">
        <div className="rounded-2xl bg-brand-light p-5">
          <div className="flex items-center gap-2 text-brand-blue font-display font-bold"><Rocket className="w-5 h-5" /> Your HR. In Flow.</div>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">Automate processes, stay compliant and empower your people.</p>
          <button onClick={onDemo} className="mt-4 w-full flex items-center justify-center gap-1.5 bg-white text-brand-blue border border-brand-skysoft/50 rounded-lg h-10 text-sm font-semibold hover:bg-white/70 transition-colors">
            Book a Demo <ChevronDown className="w-4 h-4 -rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { session, logout } = useAuth();
  const nav2 = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const user = session?.user;
  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("");

  const doLogout = () => { logout(); nav2("/"); };

  return (
    <div className="min-h-screen bg-brand-mist">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[264px] bg-white border-r border-slate-100 flex-col z-30">
        <SidebarContent onDemo={() => setDemoOpen(true)} />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[280px] bg-white h-full">
            <button className="absolute top-6 right-4 text-slate-400" onClick={() => setMobileOpen(false)}><X /></button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} onDemo={() => { setMobileOpen(false); setDemoOpen(true); }} />
          </aside>
        </div>
      )}

      <div className="lg:pl-[264px]">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-brand-mist/80 backdrop-blur-md">
          <div className="px-5 sm:px-8 h-[76px] flex items-center justify-between gap-4">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)}><Menu /></button>
            <div className="hidden sm:flex items-center gap-2 bg-white rounded-xl px-4 h-11 w-full max-w-md border border-slate-100">
              <Search className="w-4 h-4 text-slate-400" />
              <input placeholder="Search employees, documents..." className="bg-transparent outline-none text-sm w-full text-slate-700 placeholder:text-slate-400" />
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <button className="relative w-11 h-11 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-500 hover:text-brand-blue transition-colors">
                <Bell className="w-5 h-5" />
                <span className="absolute top-2 right-2 w-4 h-4 bg-brand-blue text-white text-[10px] rounded-full flex items-center justify-center">3</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2.5 outline-none">
                  <div className="w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center font-semibold overflow-hidden">
                    {user?.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : initials}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-semibold text-brand-navy leading-tight">{user?.name}</div>
                    <div className="text-xs text-slate-500">{user?.role}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{session?.organization?.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav2("/app/settings")}><Settings className="w-4 h-4 mr-2" /> Settings</DropdownMenuItem>
                  <DropdownMenuItem onClick={doLogout} className="text-destructive focus:text-destructive"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="px-5 sm:px-8 pb-12">
          <Outlet />
        </main>
      </div>
      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
